import { FC, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Button from 'components/Button';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import InfoOutline from '@mui/icons-material/InfoOutline';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { match } from 'ts-pattern';
import { Row } from 'further-ui/components/Layout';
import { useDisclosure } from 'further-ui/hooks';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import Switch from 'further-ui/components/Switch';
import { LpInvestorUpdateStatus } from 'further-types/lp/investor-update';
import { PERIOD_END_REPORT_LIST } from 'adminConstants/routes';
import { useNotification } from 'hooks/ui/useNotification';
import Page from 'components/new/Page';
import Stepper from 'components/new/Stepper';
import AddEditPeriodEndReportForm from './AddEditPeriodEndReportForm';
import Complete from './Complete';
import usePeriodEndReport from './usePeriodEndReport';
import usePeriodEndReportPreviewPdf from './usePeriodEndReportPreviewPdf';
import useCreationStep, { CreationStep } from './useCreationStep';
import { PeriodEndReportFormValues, schema } from './schema';
import {
  mapPeriodEndReportToFormValues,
  mapFormValuesToSaveInput,
  mapFormValuesToPublishInput,
} from './formMapping';
import { PeriodEndReport } from './types';

const StyledStepper = styled(Stepper)(() => ({
  '& .MuiStepConnector-line': {
    minWidth: 80,
    flexShrink: 0,
  },
}));

type Props = {
  periodEndReport: PeriodEndReport;
};

const AddEditPeriodEndReport: FC<Props> = ({ periodEndReport }) => {
  const { id: periodEndReportId } = periodEndReport;
  const { save, publish, saving } = usePeriodEndReport();
  const { downloadPreviewPdf, isDownloadingPreviewPdf } =
    usePeriodEndReportPreviewPdf();

  const history = useHistory();
  const { pathname, search } = useLocation<{ periodEndReportId?: string }>();
  const { error } = useNotification();
  const publishDialog = useDisclosure();
  const cancelDialog = useDisclosure();
  const saveAndExitWhenPublishedDialog = useDisclosure();
  const [hasCompletedCreateStep, setHasCompletedCreateStep] = useState(
    !!periodEndReportId,
  );

  const ensurePeriodEndReportIdInQuery = (id: string) => {
    const params = new URLSearchParams(search);
    if (params.get('periodEndReportId') === id) return;

    params.set('periodEndReportId', id);
    history.replace(`${pathname}?${params.toString()}`);
  };

  const formValues = mapPeriodEndReportToFormValues(periodEndReport);

  const {
    setValue,
    getValues,
    formState: { isDirty },
    ...form
  } = useForm<PeriodEndReportFormValues>({
    resolver: zodResolver(schema),
    values: formValues,
  });

  const dataIsValid = schema.safeParse({
    ...formValues,
    useFullValidation: true,
  }).success;

  const { step, setStep } = useCreationStep(
    dataIsValid || hasCompletedCreateStep,
  );

  const canNotPublish =
    step === CreationStep.ReviewAndPublish &&
    !periodEndReport.accountingPeriodClosed;
  const isDraft = periodEndReport.status === LpInvestorUpdateStatus.Draft;
  const isPublished =
    periodEndReport.status === LpInvestorUpdateStatus.Published;
  const recipients = form.watch('recipients');
  const firstInvestorId = recipients.at(0)?.investorId;
  const investorsWillBeNotified = recipients?.some((r) => r.notifyViaEmail);
  const resendPublishedUpdates = form.watch('resendPublishedUpdates');
  const emailsWillBeSent = match({
    isPublished,
    investorsWillBeNotified,
    resendPublishedUpdates,
  })
    .with(
      { isPublished: true },
      ({ investorsWillBeNotified, resendPublishedUpdates }) =>
        resendPublishedUpdates && investorsWillBeNotified,
    )
    .otherwise(() => investorsWillBeNotified);

  const handleProceed = () => {
    if (saving) return;

    setValue('useFullValidation', true);
    form.handleSubmit(
      (formData) => {
        if (!formData.useFullValidation) return;
        if (step === CreationStep.CreateUpdate) {
          setHasCompletedCreateStep(true);
          setStep(CreationStep.ReviewAndPublish);
        } else if (publishDialog.isOpen) {
          const mappedData = mapFormValuesToPublishInput(
            periodEndReport,
            formData,
          );
          publish(mappedData, {
            onSuccess: ({ id }) => {
              if (!periodEndReportId) {
                ensurePeriodEndReportIdInQuery(id);
              }
              setValue('id', id);
              publishDialog.onClose();
              setStep(CreationStep.Complete);
            },
          });
        } else {
          publishDialog.onOpen();
        }
      },
      () => {
        error('Please check the page for errors before proceeding.');
      },
    )();
  };

  const handleRetreat = () => {
    if (step === CreationStep.ReviewAndPublish) {
      setStep(CreationStep.CreateUpdate);
    } else if (
      step === CreationStep.CreateUpdate &&
      !cancelDialog.isOpen &&
      isDirty
    ) {
      cancelDialog.onOpen();
    } else {
      history.push(PERIOD_END_REPORT_LIST);
    }
  };

  const handleSaveAndExit = () => {
    if (saving) return;
    if (
      isDirty &&
      isPublished &&
      !saveAndExitWhenPublishedDialog.isOpen &&
      !cancelDialog.isOpen
    ) {
      saveAndExitWhenPublishedDialog.onOpen();
      return;
    }
    saveAndExitWhenPublishedDialog.onClose();
    setValue('useFullValidation', false);
    form.clearErrors();
    form.handleSubmit(
      (formData) => {
        const mappedData = mapFormValuesToSaveInput(periodEndReport, formData);
        save(mappedData, {
          onSuccess: ({ id }) => {
            if (!periodEndReportId) {
              ensurePeriodEndReportIdInQuery(id);
            }
            setValue('id', id);
            history.push(PERIOD_END_REPORT_LIST);
          },
        });
      },
      () => {
        error(
          'Unable to save your changes, please check the page for errors before proceeding.',
        );
      },
    )();
  };

  const handleDownloadPreviewPdf = () => {
    if (!firstInvestorId) return;
    if (isDownloadingPreviewPdf(firstInvestorId)) return;
    if (isPublished) {
      setValue('useFullValidation', true);
    } else {
      setValue('useFullValidation', false);
      form.clearErrors();
    }
    form.handleSubmit((formData) => {
      const mappedData = mapFormValuesToSaveInput(periodEndReport, formData);
      downloadPreviewPdf({
        ...mappedData,
        investorId: firstInvestorId,
      });
    })();
  };

  return (
    <Page
      tabTitle="Investor Updates: Period End report"
      goBackAction={PERIOD_END_REPORT_LIST}
      heading="Period-end report"
      withHeadingDivider
      withStickyHeader
      headerBreakpoint="sm"
      customActions={
        <StyledStepper
          steps={[
            periodEndReportId ? 'Edit update' : 'Create update',
            'Review and publish',
          ]}
          activeStep={match(step)
            .with(CreationStep.Complete, () => 2)
            .with(CreationStep.ReviewAndPublish, () => 1)
            .otherwise(() => 0)}
        />
      }
      spacing="lg"
      actionBar={
        <Row
          spacing="sm"
          justify={
            step === CreationStep.Complete ? 'flex-end' : 'space-between'
          }
          breakpoint="sm"
          reverse="breakpoint"
        >
          {step === CreationStep.Complete ? (
            <Button onClick={handleRetreat} variant="outlined" size="large">
              Exit
            </Button>
          ) : (
            <>
              <Button size="large" variant="outlined" onClick={handleRetreat}>
                {step === CreationStep.CreateUpdate
                  ? 'Cancel'
                  : 'Previous step'}
              </Button>
              <Row spacing="sm" centered breakpoint="sm" reverse="breakpoint">
                <Tooltip
                  title={
                    !firstInvestorId
                      ? 'Please add at least one investor to the recipient list before downloading a preview PDF.'
                      : ''
                  }
                >
                  <span>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="large"
                      onClick={handleDownloadPreviewPdf}
                      loading={
                        !!firstInvestorId &&
                        isDownloadingPreviewPdf(firstInvestorId)
                      }
                      disabled={!firstInvestorId}
                    >
                      Download preview PDF
                    </Button>
                  </span>
                </Tooltip>
                {isDraft ? (
                  <Button
                    variant="outlined"
                    size="large"
                    loading={saving}
                    onClick={handleSaveAndExit}
                    disabled={
                      !!firstInvestorId &&
                      isDownloadingPreviewPdf(firstInvestorId)
                    }
                  >
                    Save and exit
                  </Button>
                ) : null}
                {step === CreationStep.ReviewAndPublish && isPublished ? (
                  <Row centered>
                    <Tooltip title="Toggle this on if you want to resend email notifications to investors when you re-publish this investor report.">
                      <span>
                        <Row spacing="xxs" centered>
                          <Typography variant="h6">Resend reports</Typography>
                          <InfoOutline fontSize="inherit" />
                        </Row>
                      </span>
                    </Tooltip>
                    <Controller
                      control={form.control}
                      name="resendPublishedUpdates"
                      render={({ field }) => (
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </Row>
                ) : null}
                <Tooltip
                  title={
                    canNotPublish ? (
                      <>
                        This reporting period must be closed in{' '}
                        <em>Period-End Review</em> before the report can be
                        submitted.
                      </>
                    ) : (
                      ''
                    )
                  }
                >
                  <span>
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={handleProceed}
                      disabled={
                        canNotPublish ||
                        saving ||
                        (!!firstInvestorId &&
                          isDownloadingPreviewPdf(firstInvestorId))
                      }
                    >
                      {step === CreationStep.CreateUpdate
                        ? 'Continue'
                        : 'Publish update'}
                    </Button>
                  </span>
                </Tooltip>
              </Row>
            </>
          )}
        </Row>
      }
    >
      <ConfirmDialog
        maxWidth="sm"
        title="Exit without publishing"
        content="You are about to exit without publishing your changes. Your changes will be saved for editing later."
        actions={{
          confirm: {
            label: 'Exit page',
            onClick: handleSaveAndExit,
          },
        }}
        onClose={saveAndExitWhenPublishedDialog.onClose}
        open={saveAndExitWhenPublishedDialog.isOpen}
      />
      <ConfirmDialog
        maxWidth="sm"
        content={`You are about to publish this update and investors will${
          !emailsWillBeSent ? ' not' : ''
        } be notified by email. Do you wish to proceed?`}
        title="Publishing update"
        loading={saving}
        actions={{
          confirm: {
            onClick: handleProceed,
            label: 'Publish update',
          },
        }}
        onClose={publishDialog.onClose}
        open={publishDialog.isOpen}
      />
      <ConfirmDialog
        maxWidth="sm"
        content={
          isDraft
            ? 'You are about to leave without saving. All progress will be lost. Do you want to save before exiting?'
            : 'You are about to exit without submitting this update. Any unsaved changes will be lost.'
        }
        title="Exit update creation"
        actions={{
          alternateConfirm: isDraft
            ? {
                onClick: handleRetreat,
                label: 'Exit without saving',
              }
            : undefined,
          confirm: isDraft
            ? {
                onClick: handleSaveAndExit,
                label: 'Save and exit',
              }
            : {
                onClick: handleRetreat,
                label: 'Exit without submitting',
              },
        }}
        onClose={cancelDialog.onClose}
        open={cancelDialog.isOpen}
      />
      {step === CreationStep.Complete ? (
        <Complete />
      ) : (
        <AddEditPeriodEndReportForm
          control={form.control}
          getValues={getValues}
          setValue={setValue}
          readOnly={step === CreationStep.ReviewAndPublish}
          periodEndReport={periodEndReport}
        />
      )}
    </Page>
  );
};

export default AddEditPeriodEndReport;
